import {
  cancelarProcessoArbitral,
  criarSolicitacaoArbitral,
  enviarMensagemChat,
  resolverPendencia,
} from '@/features/arbitral-detalhes/store/actions';
import {
  baixarCertificadoPainel,
  baixarDocumentoPainel,
  emitirTermoImparcialidadePainel,
  emitirTermosLotePainel,
  publicarDocumentoEmissaoPainel,
  salvarRascunhoEmissaoPainel,
} from '@/features/arbitro/store/actions';
import { aceitarNomeacao, recusarNomeacao } from '@/features/arbitroNomeacoes/store/actions';
import { assinarSolicitacaoViaToken } from '@/features/assinatura/store/actions';
import { sendMessage as enviarMensagemAtendimento } from '@/features/chatAtendimento/store/messages/actions';
import { baixarDocumento, visualizarDocumento } from '@/features/documentos/store/actions';
import {
  confirmarEnvio,
  criarIndividual,
  criarLote,
  criarLoteComMapeamento,
  criarLoteRascunho,
  reenviarItem,
} from '@/features/extrajudicial/store/actions';
import { improveTemplate } from '@/features/ia/store/slice';
import {
  encerrarNegociacaoDetail,
  reabrirNegociacaoDetail,
} from '@/features/negociacaoDetalhes/store/actions';
import { createNegociacaoIndividual } from '@/features/negociacaoIndividualWizard/store/actions';
import {
  confirmLoteNegociacao,
  encerrarNegociacaoCaso,
  reabrirNegociacaoCaso,
} from '@/features/negociacoes/store/actions';
import {
  portalCompleteOnboarding,
  portalConsumeMagicLink,
  portalDownloadDocumento,
  portalGetDetail,
  portalResponder,
  portalUploadAnexo,
} from '@/features/portal/store/actions';
import {
  carregarPortalAdvogado,
  carregarProcessoAdvogado,
  concluirCadastroAdvogado,
  enviarProcuracaoAdvogado,
} from '@/features/portal-advogado/store/actions';
import {
  criarSolicitacaoAdvogado,
  enviarMensagemAdvogado,
  registrarAndamentoAdvogado,
  responderPendenciaAdvogado,
} from '@/features/portal-advogado/store/autosActions';
import {
  arbitragemResolverPendencia,
  citacaoArbitralConsumeMagicLink,
  citacaoArbitralSubmit,
  citacaoArbitralUploadAnexo,
  convidarAdvogadoPorEmail,
} from '@/features/portal-arbitral/store/actions';
import { submitProcessoArbitral } from '@/features/processo-arbitral/store/submissaoStep/actions';
import {
  criarCobrancaIndividual,
  encerrarCobranca,
  pausarCobranca,
  registrarAcordoCobranca,
  registrarPagamentoCobranca,
} from '@/features/reguaCobranca/store/actions';
import type { PostHogBusinessEvent } from '@/shared/types/posthog';

/**
 * Evento de negócio por action `fulfilled`. As chaves saem do próprio thunk, e não de string
 * literal, para que renomear ou apagar um thunk quebre o typecheck em vez de silenciar o evento —
 * nenhum teste percebe um gatilho que simplesmente deixou de acontecer.
 *
 * Só entra thunk que alguma tela despacha de fato: definição no catálogo do PostHog sem gatilho
 * real aparece para o Produto como "zero uso", não como "sem coleta". O mapa completo, com o
 * momento de cada evento e os que ficaram fora, está em
 * `documentation/analytics/posthog-eventos-produtos.md`.
 */
export const posthogBusinessEvents: Readonly<Record<string, PostHogBusinessEvent>> = {
  [criarIndividual.fulfilled.type]: {
    event: 'notificacao_individual_criada',
    product: 'notificacao',
  },
  [criarLoteRascunho.fulfilled.type]: {
    event: 'notificacao_lote_rascunho_criado',
    product: 'notificacao',
  },
  // Os dois modos de upload (com e sem mapeamento de colunas) são o mesmo marco para o Produto.
  [criarLote.fulfilled.type]: { event: 'notificacao_lote_criado', product: 'notificacao' },
  [criarLoteComMapeamento.fulfilled.type]: {
    event: 'notificacao_lote_criado',
    product: 'notificacao',
  },
  [confirmarEnvio.fulfilled.type]: {
    event: 'notificacao_envio_confirmado',
    product: 'notificacao',
  },
  [reenviarItem.fulfilled.type]: {
    event: 'notificacao_reenvio_solicitado',
    product: 'notificacao',
  },

  [createNegociacaoIndividual.fulfilled.type]: {
    event: 'negociacao_individual_criada',
    product: 'negociacao',
  },
  [confirmLoteNegociacao.fulfilled.type]: {
    event: 'negociacao_lote_confirmado',
    product: 'negociacao',
  },
  // Encerrar e reabrir existem na lista (negociacoes) e na tela de detalhe (negociacaoDetalhes),
  // com thunks distintos; contar só um deles subestimaria o uso.
  [encerrarNegociacaoCaso.fulfilled.type]: { event: 'negociacao_encerrada', product: 'negociacao' },
  [encerrarNegociacaoDetail.fulfilled.type]: {
    event: 'negociacao_encerrada',
    product: 'negociacao',
  },
  [reabrirNegociacaoCaso.fulfilled.type]: { event: 'negociacao_reaberta', product: 'negociacao' },
  [reabrirNegociacaoDetail.fulfilled.type]: { event: 'negociacao_reaberta', product: 'negociacao' },

  [criarCobrancaIndividual.fulfilled.type]: { event: 'cobranca_criada', product: 'regua_cobranca' },
  [pausarCobranca.fulfilled.type]: { event: 'cobranca_pausada', product: 'regua_cobranca' },
  [registrarAcordoCobranca.fulfilled.type]: {
    event: 'cobranca_acordo_registrado',
    product: 'regua_cobranca',
  },
  [registrarPagamentoCobranca.fulfilled.type]: {
    event: 'cobranca_pagamento_registrado',
    product: 'regua_cobranca',
  },
  [encerrarCobranca.fulfilled.type]: { event: 'cobranca_encerrada', product: 'regua_cobranca' },

  [submitProcessoArbitral.fulfilled.type]: { event: 'arbitragem_submetida', product: 'arbitragem' },
  [resolverPendencia.fulfilled.type]: {
    event: 'arbitragem_pendencia_respondida',
    product: 'arbitragem',
  },
  [criarSolicitacaoArbitral.fulfilled.type]: {
    event: 'arbitragem_solicitacao_criada',
    product: 'arbitragem',
  },
  [enviarMensagemChat.fulfilled.type]: {
    event: 'arbitragem_mensagem_enviada',
    product: 'arbitragem',
  },
  [cancelarProcessoArbitral.fulfilled.type]: {
    event: 'arbitragem_cancelada',
    product: 'arbitragem',
  },

  [visualizarDocumento.fulfilled.type]: { event: 'documento_visualizado', product: 'documentos' },
  [baixarDocumento.fulfilled.type]: { event: 'documento_baixado', product: 'documentos' },

  [assinarSolicitacaoViaToken.fulfilled.type]: {
    event: 'assinatura_realizada',
    product: 'assinatura',
  },

  [enviarMensagemAtendimento.fulfilled.type]: {
    event: 'atendimento_mensagem_enviada',
    product: 'atendimento',
  },

  [improveTemplate.fulfilled.type]: { event: 'ia_modelo_melhorado', product: 'ia' },

  [portalCompleteOnboarding.fulfilled.type]: {
    event: 'portal_cadastro_concluido',
    product: 'portal',
  },
  [portalConsumeMagicLink.fulfilled.type]: {
    event: 'portal_acesso_magic_link_realizado',
    product: 'portal',
  },
  [portalResponder.fulfilled.type]: { event: 'portal_manifestacao_enviada', product: 'portal' },
  [portalGetDetail.fulfilled.type]: { event: 'notificado_detalhe_carregado', product: 'portal' },
  [portalUploadAnexo.fulfilled.type]: { event: 'notificado_anexo_enviado', product: 'portal' },
  [portalDownloadDocumento.fulfilled.type]: {
    event: 'notificado_documento_obtido',
    product: 'portal',
  },

  [citacaoArbitralConsumeMagicLink.fulfilled.type]: {
    event: 'notificado_arbitragem_acesso_realizado',
    product: 'portal_arbitral',
  },
  [citacaoArbitralSubmit.fulfilled.type]: {
    event: 'notificado_arbitragem_manifestacao_enviada',
    product: 'portal_arbitral',
  },
  [arbitragemResolverPendencia.fulfilled.type]: {
    event: 'notificado_arbitragem_pendencia_respondida',
    product: 'portal_arbitral',
  },
  [citacaoArbitralUploadAnexo.fulfilled.type]: {
    event: 'notificado_arbitragem_anexo_enviado',
    product: 'portal_arbitral',
  },
  [convidarAdvogadoPorEmail.fulfilled.type]: {
    event: 'notificado_advogado_convidado',
    product: 'portal_arbitral',
  },

  [concluirCadastroAdvogado.fulfilled.type]: {
    event: 'advogado_cadastro_concluido',
    product: 'portal_advogado',
  },
  [carregarPortalAdvogado.fulfilled.type]: {
    event: 'advogado_painel_carregado',
    product: 'portal_advogado',
  },
  [carregarProcessoAdvogado.fulfilled.type]: {
    event: 'advogado_processo_carregado',
    product: 'portal_advogado',
  },
  [enviarProcuracaoAdvogado.fulfilled.type]: {
    event: 'advogado_procuracao_enviada',
    product: 'portal_advogado',
  },
  [registrarAndamentoAdvogado.fulfilled.type]: {
    event: 'advogado_andamento_registrado',
    product: 'portal_advogado',
  },
  [criarSolicitacaoAdvogado.fulfilled.type]: {
    event: 'advogado_solicitacao_criada',
    product: 'portal_advogado',
  },
  [responderPendenciaAdvogado.fulfilled.type]: {
    event: 'advogado_pendencia_respondida',
    product: 'portal_advogado',
  },
  [enviarMensagemAdvogado.fulfilled.type]: {
    event: 'advogado_mensagem_enviada',
    product: 'portal_advogado',
  },

  [aceitarNomeacao.fulfilled.type]: { event: 'arbitro_nomeacao_aceita', product: 'painel_arbitro' },
  [recusarNomeacao.fulfilled.type]: {
    event: 'arbitro_nomeacao_recusada',
    product: 'painel_arbitro',
  },
  [baixarDocumentoPainel.fulfilled.type]: {
    event: 'arbitro_documento_baixado',
    product: 'painel_arbitro',
  },
  [baixarCertificadoPainel.fulfilled.type]: {
    event: 'arbitro_certificado_baixado',
    product: 'painel_arbitro',
  },
  [emitirTermoImparcialidadePainel.fulfilled.type]: {
    event: 'arbitro_termo_imparcialidade_emitido',
    product: 'painel_arbitro',
  },
  [salvarRascunhoEmissaoPainel.fulfilled.type]: {
    event: 'arbitro_documento_rascunho_salvo',
    product: 'painel_arbitro',
  },
  [publicarDocumentoEmissaoPainel.fulfilled.type]: {
    event: 'arbitro_documento_publicado',
    product: 'painel_arbitro',
  },
  [emitirTermosLotePainel.fulfilled.type]: {
    event: 'arbitro_termos_lote_emitidos',
    product: 'painel_arbitro',
  },
};
